import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, Radio, Space } from "antd";
import { CheckCircleOutlined, SwapOutlined } from "@ant-design/icons";
import { SorterPlanSummary } from "../../../types/sortingMachine";
import useLang from "../../../hooks/useLang";

const PRIMARY_BUTTON_STYLE = {
	backgroundColor: "#4F16C2",
	borderColor: "#4F16C2",
	color: "#ffffff",
} as const;

interface ActivePlanBannerProps {
	plans: SorterPlanSummary[];
	activePlan: SorterPlanSummary | null;
	onChangeActivePlan: (planId: number) => void;
}

const ActivePlanBanner = ({
	plans,
	activePlan,
	onChangeActivePlan,
}: ActivePlanBannerProps) => {
	const { strings } = useLang("sortingMachineTab");
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const nonActivePlans = useMemo(
		() => plans.filter((p) => !p.isActive),
		[plans]
	);

	const selectedPlanName = useMemo(() => {
		const plan = plans.find((p) => p.id === selectedPlanId);
		return plan?.name ?? "";
	}, [plans, selectedPlanId]);

	const handleOpenModal = useCallback(() => {
		setSelectedPlanId(null);
		setModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpen(false);
		setSelectedPlanId(null);
	}, []);

	const handlePlanSelect = useCallback((planId: number) => {
		setSelectedPlanId(planId);
	}, []);

	const handleProceedToConfirm = useCallback(() => {
		if (selectedPlanId === null) return;
		setModalOpen(false);
		setConfirmOpen(true);
	}, [selectedPlanId]);

	const handleConfirm = useCallback(() => {
		if (selectedPlanId === null) return;
		onChangeActivePlan(selectedPlanId);
		setConfirmOpen(false);
		setSelectedPlanId(null);
	}, [selectedPlanId, onChangeActivePlan]);

	const handleCancelConfirm = useCallback(() => {
		setConfirmOpen(false);
		setSelectedPlanId(null);
	}, []);

	return (
		<>
			<div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-5 py-3">
				<div className="flex items-center gap-3">
					<CheckCircleOutlined className="text-green-600 text-xl" />
					<div>
						<span className="text-xs text-gray-500 uppercase tracking-wide">
							{strings?.banner?.label ?? "Active Plan"}
						</span>
						<div className="text-base font-semibold text-gray-800">
							{activePlan?.name ??
								strings?.banner?.noPlan ??
								"No active plan"}
						</div>
					</div>
				</div>

				<Button
					icon={<SwapOutlined />}
					onClick={handleOpenModal}
					disabled={nonActivePlans.length === 0}
				>
					{strings?.banner?.change ?? "Change Active Plan"}
				</Button>
			</div>

			<Modal
				title={
					strings?.banner?.selectTitle ?? "Select a Plan to Activate"
				}
				open={modalOpen}
				onCancel={handleCloseModal}
				onOk={handleProceedToConfirm}
				okButtonProps={{
					disabled: selectedPlanId === null,
					style: PRIMARY_BUTTON_STYLE,
				}}
				okText={strings?.banner?.proceed ?? "Proceed"}
				cancelText={strings?.banner?.cancel ?? "Cancel"}
			>
				{nonActivePlans.length === 0 ? (
					<p className="text-gray-500">
						{strings?.banner?.noOtherPlans ??
							"No other plans available."}
					</p>
				) : (
					<Radio.Group
						value={selectedPlanId}
						onChange={(e) => handlePlanSelect(e.target.value)}
						className="w-full"
					>
						<Space direction="vertical" className="w-full">
							{nonActivePlans.map((plan) => (
								<Radio
									key={plan.id}
									value={plan.id}
									className="w-full py-2 px-3 rounded border border-gray-100 hover:bg-gray-50"
								>
									{plan.name}
								</Radio>
							))}
						</Space>
					</Radio.Group>
				)}
			</Modal>

			<Modal
				title={
					strings?.banner?.confirmTitle ??
					"Confirm Active Plan Change"
				}
				open={confirmOpen}
				onOk={handleConfirm}
				onCancel={handleCancelConfirm}
				okText={strings?.banner?.confirm ?? "Yes, change it"}
				cancelText={strings?.banner?.cancel ?? "Cancel"}
				okButtonProps={{ danger: true }}
			>
				<p>
					{strings?.banner?.confirmMessage ??
						"Are you sure you want to change the active plan to"}{" "}
					<strong>{selectedPlanName}</strong>?
				</p>
			</Modal>
		</>
	);
};

export default ActivePlanBanner;
